import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function DriverSignupPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    address: '',
    vehicle_type: 'saloon',
    license_plate: '',
    driver_type: 'dsa',
    // Licenses
    dvla_licence: '',
    dvla_licence_expiry: '',
    phd_licence: '',
    phd_licence_expiry: '',
    phv_licence: '',
    phv_licence_expiry: '',
    insurance_certificate: '',
    insurance_certificate_expiry: '',
    // Additional info
    ni_number: '',
    bank_name: '',
    bank_account_number: '',
    bank_sort_code: '',
    car_make: '',
    car_model: '',
    car_colour: '',
    max_seat: 4
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/api/driver/signup`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        address: formData.address,
        vehicle_type: formData.vehicle_type,
        license_plate: formData.license_plate,
        driver_type: formData.driver_type,
        dvla_licence: formData.dvla_licence,
        dvla_licence_expiry: formData.dvla_licence_expiry,
        phd_licence: formData.phd_licence,
        phd_licence_expiry: formData.phd_licence_expiry,
        phv_licence: formData.phv_licence,
        phv_licence_expiry: formData.phv_licence_expiry,
        insurance_certificate: formData.insurance_certificate,
        insurance_certificate_expiry: formData.insurance_certificate_expiry,
        ni_number: formData.ni_number,
        bank_name: formData.bank_name,
        bank_account_number: formData.bank_account_number,
        bank_sort_code: formData.bank_sort_code,
        car_make: formData.car_make,
        car_model: formData.car_model,
        car_colour: formData.car_colour,
        max_seat: formData.max_seat
      });

      if (response.data.success) {
        localStorage.setItem('driver_token', response.data.token);
        localStorage.setItem('driver_data', JSON.stringify(response.data.driver));
        toast.success('Registration successful!');
        navigate('/driver/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 via-blue-600 to-purple-800 py-8">
      <Card className="w-full max-w-2xl mx-4">
        <CardHeader>
          <CardTitle className="text-2xl text-center">Driver Registration</CardTitle>
          <p className="text-center text-gray-600">Create your driver account</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="John Doe"
                  required
                />
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="john@example.com"
                  required
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="+447987654321"
                  required
                />
              </div>

              <div>
                <Label htmlFor="license_plate">License Plate *</Label>
                <Input
                  id="license_plate"
                  value={formData.license_plate}
                  onChange={(e) => handleChange('license_plate', e.target.value.toUpperCase())}
                  placeholder="ABC123"
                  required
                />
              </div>

              <div>
                <Label htmlFor="vehicle_type">Vehicle Type *</Label>
                <Select value={formData.vehicle_type} onValueChange={(value) => handleChange('vehicle_type', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="saloon">Saloon</SelectItem>
                    <SelectItem value="estate">Estate</SelectItem>
                    <SelectItem value="mvp">MVP</SelectItem>
                    <SelectItem value="luxury">Luxury</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                {/* Empty div for grid alignment */}
              </div>
            </div>

            <div>
              <Label htmlFor="address">Address *</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="123 Main St, London"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder="Enter password"
                  required
                />
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirm Password *</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  placeholder="Confirm password"
                  required
                />
              </div>
            </div>

            {/* Additional Information */}
            <div className="border-t pt-4 mt-4">
              <h3 className="text-lg font-semibold mb-4">Additional Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="ni_number">NI Number</Label>
                  <Input id="ni_number" placeholder="AB123456C" value={formData.ni_number} onChange={(e) => handleChange('ni_number', e.target.value.toUpperCase())} />
                </div>
                <div>
                  <Label htmlFor="bank_name">Bank Name</Label>
                  <Input id="bank_name" placeholder="Barclays" value={formData.bank_name} onChange={(e) => handleChange('bank_name', e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="bank_account_number">Account Number</Label>
                  <Input id="bank_account_number" placeholder="12345678" value={formData.bank_account_number} onChange={(e) => handleChange('bank_account_number', e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="bank_sort_code">Sort Code</Label>
                  <Input id="bank_sort_code" placeholder="12-34-56" value={formData.bank_sort_code} onChange={(e) => handleChange('bank_sort_code', e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="car_make">Car Make</Label>
                  <Input id="car_make" placeholder="Toyota" value={formData.car_make} onChange={(e) => handleChange('car_make', e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="car_model">Car Model</Label>
                  <Input id="car_model" placeholder="Corolla" value={formData.car_model} onChange={(e) => handleChange('car_model', e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="car_colour">Car Colour</Label>
                  <Input id="car_colour" placeholder="Black" value={formData.car_colour} onChange={(e) => handleChange('car_colour', e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="max_seat">Max Seats</Label>
                  <Input id="max_seat" type="number" min="1" max="8" placeholder="4" value={formData.max_seat} onChange={(e) => handleChange('max_seat', parseInt(e.target.value) || 4)} />
                </div>
              </div>
            </div>

            {/* License Documents */}
            <div className="border-t pt-4 mt-4">
              <h3 className="text-lg font-semibold mb-4">License Documents</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 p-3 bg-blue-50 rounded">
                  <div>
                    <Label>DVLA Licence</Label>
                    <Input type="file" accept="image/*,application/pdf" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { handleChange('dvla_licence', reader.result); }; reader.readAsDataURL(file); } }} className="cursor-pointer" />
                  </div>
                  <div>
                    <Label>DVLA Expiry</Label>
                    <Input type="date" value={formData.dvla_licence_expiry} onChange={(e) => handleChange('dvla_licence_expiry', e.target.value)} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 p-3 bg-green-50 rounded">
                  <div>
                    <Label>PHD Licence</Label>
                    <Input type="file" accept="image/*,application/pdf" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { handleChange('phd_licence', reader.result); }; reader.readAsDataURL(file); } }} className="cursor-pointer" />
                  </div>
                  <div>
                    <Label>PHD Expiry</Label>
                    <Input type="date" value={formData.phd_licence_expiry} onChange={(e) => handleChange('phd_licence_expiry', e.target.value)} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 p-3 bg-yellow-50 rounded">
                  <div>
                    <Label>PHV Licence</Label>
                    <Input type="file" accept="image/*,application/pdf" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { handleChange('phv_licence', reader.result); }; reader.readAsDataURL(file); } }} className="cursor-pointer" />
                  </div>
                  <div>
                    <Label>PHV Expiry</Label>
                    <Input type="date" value={formData.phv_licence_expiry} onChange={(e) => handleChange('phv_licence_expiry', e.target.value)} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 p-3 bg-purple-50 rounded">
                  <div>
                    <Label>Insurance Certificate</Label>
                    <Input type="file" accept="image/*,application/pdf" onChange={(e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { handleChange('insurance_certificate', reader.result); }; reader.readAsDataURL(file); } }} className="cursor-pointer" />
                  </div>
                  <div>
                    <Label>Insurance Expiry</Label>
                    <Input type="date" value={formData.insurance_certificate_expiry} onChange={(e) => handleChange('insurance_certificate_expiry', e.target.value)} />
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                📄 You and admin will receive notifications 1 month and 15 days before any document expires.
              </p>
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>

            <div className="text-center mt-4">
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/driver/login')}
                  className="text-purple-600 hover:underline"
                >
                  Sign In
                </button>
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default DriverSignupPage;
